import { useTranslation } from '@mint-lib/i18n';
import {
  RadioGroup,
  RadioGroupItem,
  TruncateText,
  typographyVariants,
} from '@mint-lib/tw-components';

import {
  useSetFooterRowsCount,
  useTableDataFileHeaderRows,
  useUpdateDataFileHeader,
} from '../../queries/index.js';
import {
  TableHeadSkeleton,
  TableSkeletonRows,
} from '../molecules/DynamicTableSkeleton.jsx';
import {
  Table,
  TableBody,
  TableBodyCell,
  TableContainer,
  TableRow,
} from '../molecules/TableComponents.jsx';

type HeaderTabProps = {
  dataFileId: string;
};

export const HeaderTab = ({ dataFileId }: HeaderTabProps) => {
  const { t } = useTranslation('@myn/import-hub');

  const { data: tableDataFilePreviewRows, isLoading } =
    useTableDataFileHeaderRows(dataFileId, { throwOnError: true });

  const { mutateAsync: updateTableDataFileHeader } =
    useUpdateDataFileHeader(dataFileId);
  const { mutateAsync: setFooterRowsCount } = useSetFooterRowsCount(dataFileId);

  const currentHeaderId =
    tableDataFilePreviewRows.items.find((row) => row.isHeader)?.id ?? '';

  const handleSelectHeader = async (rowId: string) => {
    try {
      await updateTableDataFileHeader({ payload: { rowId } });
      await setFooterRowsCount({ footerRowsCount: 0 });
    } catch {
      /* empty */
    }
  };

  const maxCellsCount = tableDataFilePreviewRows.items.reduce((acc, row) => {
    return Math.max(acc, row.cells.length);
  }, 0);

  const normalizeCellsCount = (row: { cells: string[]; id: string }) => {
    const missingCellsCount = maxCellsCount - row.cells.length;
    const cells = row.cells.map((value, index) => ({
      key: `${row.id}-cell-${index}`,
      value,
    }));

    if (missingCellsCount > 0) {
      return [
        ...cells,
        ...Array.from({ length: missingCellsCount }, (_, index) => ({
          key: `${row.id}-empty-cell-${row.cells.length + index}`,
          value: '',
        })),
      ];
    }
    return cells;
  };

  return (
    <TableContainer className="shrink-[1000] bg-ui-primary">
      <div className="flex h-10 items-center px-4">
        <h6 className={typographyVariants({ variant: 'h6' })}>
          {t('Your header')}
        </h6>
      </div>
      <RadioGroup value={currentHeaderId} className="contents">
        <Table className="table-fixed shadow-none">
          {isLoading && <TableHeadSkeleton />}
          <TableBody>
            {isLoading ? (
              <TableSkeletonRows rowsCount={20} />
            ) : (
              tableDataFilePreviewRows.items.map((row) => (
                <TableRow
                  hover
                  key={row.id}
                  selected={row.id === currentHeaderId}
                  aria-checked={row.id === currentHeaderId}
                  className={
                    row.id === currentHeaderId
                      ? '[&>td]:bg-ui-selected'
                      : undefined
                  }
                >
                  <TableBodyCell align="center" className="w-10 p-0">
                    <RadioGroupItem
                      value={row.id}
                      onClick={() => void handleSelectHeader(row.id)}
                      aria-label={t('File header')}
                    />
                  </TableBodyCell>
                  {normalizeCellsCount(row).map((cell) => (
                    <TableBodyCell key={cell.key} className="w-[12.5rem]">
                      <TruncateText>{cell.value}</TruncateText>
                    </TableBodyCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </RadioGroup>
    </TableContainer>
  );
};
